import type { MediaRef } from "@rhema/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useClient } from "../session/session";

type MediaState = { url: string | null; blob: Blob | null; loading: boolean; error: string | null };

/**
 * Decrypts a file and gives back an object URL for it. A file you are still sending is shown
 * from the local copy. With `auto` off nothing downloads until `load` is called.
 */
export function useMediaUrl(ref: MediaRef | undefined, local?: Blob, auto = true) {
  const client = useClient();
  const [state, setState] = useState<MediaState>({ url: null, blob: null, loading: false, error: null });
  const urlRef = useRef<string | null>(null);
  const live = useRef(true);

  const show = useCallback((blob: Blob) => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    const url = URL.createObjectURL(blob);
    urlRef.current = url;
    if (live.current) setState({ url, blob, loading: false, error: null });
    return blob;
  }, []);

  // A new ref object arrives with every render. The storage key says whether it is a new file.
  const latest = useRef(ref);
  latest.current = ref;
  const key = ref?.key;
  const load = useCallback(async (): Promise<Blob | null> => {
    if (local) return show(local);
    const current = latest.current;
    if (!key || !current) return null;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      return show(await client.media.download(current));
    } catch (err) {
      if (live.current) setState({ url: null, blob: null, loading: false, error: (err as Error).message });
      return null;
    }
  }, [client, key, local, show]);

  useEffect(() => {
    live.current = true;
    if (local || auto) void load();
    return () => {
      live.current = false;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    };
  }, [load, local, auto]);

  return { ...state, load };
}
